import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOK_URL, LOGO_URL } from "@/lib/site";

const links = [
  { href: "#services", label: "Services" },
  { href: "#precommandes", label: "Précommandes" },
  { href: "#a-propos", label: "À propos" },
  { href: "#paiement", label: "Paiement" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onLinkClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-pink-100 shadow-[0_4px_30px_rgba(255,0,127,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
        <a
          href="#"
          data-testid="navbar-logo"
          className="flex items-center gap-3"
          onClick={(e) => onLinkClick(e, "#")}
        >
          <img
            src={LOGO_URL}
            alt="LaGigaFam by Perle Lama"
            className="h-14 w-14 rounded-2xl object-contain bg-black p-1"
          />
          <div className="leading-none">
            <div className="text-[11px] font-extrabold uppercase tracking-tight text-pink-600">
              LAGIGAFAM
            </div>
            <div className="font-display text-base md:text-lg font-black tracking-tight text-neutral-900">
              BBL NATURAL SCULPT
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => onLinkClick(e, l.href)}
              className="text-sm font-semibold text-neutral-800 hover:text-pink-600 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button
            asChild
            data-testid="navbar-book-button"
            className="rounded-full bg-pink-600 hover:bg-pink-700 text-white px-6 h-11 font-bold shadow-[0_8px_24px_rgba(255,0,127,0.35)] hover:scale-[1.03] transition-transform"
          >
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
              Réserver ✨
            </a>
          </Button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          aria-label="menu"
          className="md:hidden h-11 w-11 rounded-full bg-white border border-pink-100 flex items-center justify-center"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-pink-100 px-5 py-5 flex flex-col gap-3">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => onLinkClick(e, l.href)}
              className="py-2 font-semibold text-neutral-800"
            >
              {l.label}
            </a>
          ))}
          <Button
            asChild
            data-testid="mobile-book-button"
            className="rounded-full bg-pink-600 hover:bg-pink-700 text-white w-full h-12 font-bold"
          >
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
              Réserver ✨
            </a>
          </Button>
        </div>
      )}
    </header>
  );
}
